/**
 * User-preference store.
 *
 * Persists theme / tenant / locale. Today this is localStorage-backed; the
 * interface is deliberately async-shaped so a server-backed Settings API can
 * drop in later without touching callers (the driver just becomes a fetch).
 */
import { DEFAULT_THEME, DEFAULT_TENANT, isTheme, isTenant, type Theme, type Tenant } from "@oxbo/tokens";
import { DEFAULT_LOCALE, isLocale, type Locale } from "@i18n/locales";

export interface Preferences {
  theme: Theme;
  tenant: Tenant;
  locale: Locale;
  /** Left-nav collapsed (icon-only rail) vs expanded (icon + label). */
  navCollapsed: boolean;
}

const KEYS = {
  theme: "oxbo.theme",
  tenant: "oxbo.tenant",
  locale: "oxbo.locale",
  navCollapsed: "oxbo.navCollapsed",
} as const;

function safeGet(key: string): string | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
  } catch {
    /* storage disabled (private mode / quota) — degrade to in-memory only */
  }
}

export function loadPreferences(): Preferences {
  const theme = safeGet(KEYS.theme);
  const tenant = safeGet(KEYS.tenant);
  const locale = safeGet(KEYS.locale);
  const navCollapsed = safeGet(KEYS.navCollapsed);
  return {
    theme: isTheme(theme) ? theme : DEFAULT_THEME,
    tenant: isTenant(tenant) ? tenant : DEFAULT_TENANT,
    locale: isLocale(locale) ? locale : DEFAULT_LOCALE,
    // Default expanded; only an explicit "true" collapses the rail.
    navCollapsed: navCollapsed === "true",
  };
}

export function saveTheme(theme: Theme): void {
  safeSet(KEYS.theme, theme);
}
export function saveTenant(tenant: Tenant): void {
  safeSet(KEYS.tenant, tenant);
}
export function saveLocale(locale: Locale): void {
  safeSet(KEYS.locale, locale);
}
export function saveNavCollapsed(collapsed: boolean): void {
  safeSet(KEYS.navCollapsed, collapsed ? "true" : "false");
}
